import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import ImageUpload from '../../components/ImageUpload';

const ChurchCreateEventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { church } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [fetchingEvent, setFetchingEvent] = useState(!!id);
  const [organizations, setOrganizations] = useState([]);
  const [venues, setVenues] = useState([]);
  const [error, setError] = useState('');
  const [showVenueModal, setShowVenueModal] = useState(false);
  const [savingVenue, setSavingVenue] = useState(false);
  const [venueForm, setVenueForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    capacity: '',
    website_url: '',
    description: ''
  });

  const [form, setForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    venue_id: '',
    location: '',
    organization_id: '',
    custom_organization: '',
    website_url: '',
    registration_url: '',
    image_url: '',
    is_paid_event: false,
    registration_fee: '',
    early_bird_fee: '',
    early_bird_deadline: '',
    max_attendees: '',
    event_type: 'in_person',
    virtual_platform: '',
    virtual_url: '',
    virtual_meeting_id: '',
    virtual_passcode: '',
    event_audience: 'general',
    event_category: 'conference'
  });

  useEffect(() => {
    fetchOrganizations();
    fetchVenues();
    if (id) {
      fetchEvent();
    }
  }, [id]);

  const fetchOrganizations = async () => {
    const { data } = await supabase.from('organizations').select('*').order('name');
    setOrganizations(data || []);
  };

  const fetchVenues = async () => {
    const { data } = await supabase.from('venues').select('*').order('name');
    setVenues(data || []);
  };

  const fetchEvent = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .eq('created_by', user.id)
        .single();

      if (error) throw error;
      if (!data) {
        navigate('/church/events');
        return;
      }

      setForm({
        title: data.title || '',
        description: data.description || '',
        start_date: data.start_date?.split('T')[0] || '',
        end_date: data.end_date?.split('T')[0] || '',
        venue_id: data.venue_id || '',
        location: data.location || '',
        organization_id: data.custom_organization ? 'other' : (data.organization_id || ''),
        custom_organization: data.custom_organization || '',
        website_url: data.website_url || '',
        registration_url: data.registration_url || '',
        image_url: data.image_url || '',
        is_paid_event: data.is_paid_event || false,
        registration_fee: data.registration_fee || '',
        early_bird_fee: data.early_bird_fee || '',
        early_bird_deadline: data.early_bird_deadline?.split('T')[0] || '',
        max_attendees: data.max_attendees || '',
        event_type: data.event_type || 'in_person',
        virtual_platform: data.virtual_platform || '',
        virtual_url: data.virtual_url || '',
        virtual_meeting_id: data.virtual_meeting_id || '',
        virtual_passcode: data.virtual_passcode || '',
        event_audience: data.event_audience || 'general',
        event_category: data.event_category || 'conference'
      });
    } catch (err) {
      console.error('Error fetching event:', err);
      navigate('/church/events');
    } finally {
      setFetchingEvent(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVenueFormChange = (e) => {
    const { name, value } = e.target;
    setVenueForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddVenue = async (e) => {
    e.preventDefault();
    if (!venueForm.name || !venueForm.city || !venueForm.state) {
      alert('Please fill in venue name, city, and state');
      return;
    }

    setSavingVenue(true);
    try {
      const { data, error } = await supabase
        .from('venues')
        .insert({
          name: venueForm.name,
          address: venueForm.address || null,
          city: venueForm.city,
          state: venueForm.state,
          zip_code: venueForm.zip_code || null,
          capacity: venueForm.capacity ? parseInt(venueForm.capacity) : null,
          website_url: venueForm.website_url || null,
          description: venueForm.description || null,
          created_by: user.id,
          church_id: church?.id || null
        })
        .select()
        .single();

      if (error) throw error;

      // Add new venue to list and select it
      setVenues(prev => [...prev, data]);
      setForm(prev => ({ ...prev, venue_id: data.id, location: `${data.city}, ${data.state}` }));
      setShowVenueModal(false);
      setVenueForm({
        name: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        capacity: '',
        website_url: '',
        description: ''
      });
    } catch (err) {
      console.error('Error creating venue:', err);
      alert('Failed to create venue. Please try again.');
    } finally {
      setSavingVenue(false);
    }
  };

  const prefillChurchAsVenue = () => {
    if (church) {
      setVenueForm({
        name: church.name || '',
        address: church.address || '',
        city: church.city || '',
        state: church.state || '',
        zip_code: '',
        capacity: '',
        website_url: church.website_url || '',
        description: ''
      });
    }
    setShowVenueModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const eventData = {
        title: form.title,
        description: form.description,
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        venue_id: form.venue_id || null,
        location: form.location || null,
        organization_id: form.organization_id === 'other' ? null : (form.organization_id || null),
        custom_organization: form.organization_id === 'other' ? form.custom_organization : (church?.name || profile?.church_name || null),
        website_url: form.website_url || null,
        registration_url: form.registration_url || null,
        image_url: form.image_url || null,
        is_paid_event: form.is_paid_event,
        registration_fee: form.is_paid_event && form.registration_fee ? parseFloat(form.registration_fee) : null,
        early_bird_fee: form.is_paid_event && form.early_bird_fee ? parseFloat(form.early_bird_fee) : null,
        early_bird_deadline: form.is_paid_event && form.early_bird_deadline ? form.early_bird_deadline : null,
        max_attendees: form.max_attendees ? parseInt(form.max_attendees) : null,
        event_type: form.event_type || 'in_person',
        virtual_platform: form.event_type !== 'in_person' ? (form.virtual_platform || null) : null,
        virtual_url: form.event_type !== 'in_person' ? (form.virtual_url || null) : null,
        virtual_meeting_id: form.event_type !== 'in_person' ? (form.virtual_meeting_id || null) : null,
        virtual_passcode: form.event_type !== 'in_person' ? (form.virtual_passcode || null) : null,
        event_audience: form.event_audience || 'general',
        event_category: form.event_category || 'conference',
        church_id: church?.id || null,
        created_by: user.id,
        submitter_name: profile?.full_name,
        submitter_email: user.email
      };

      if (id) {
        // Update existing event
        const { error } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', id)
          .eq('created_by', user.id);

        if (error) throw error;
      } else {
        // Create new event
        eventData.status = 'pending';
        eventData.submitted_at = new Date().toISOString();

        const { error } = await supabase
          .from('events')
          .insert(eventData);

        if (error) throw error;
      }

      navigate('/church/events');
    } catch (err) {
      console.error('Error saving event:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingEvent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {id ? 'Edit Event' : 'Create New Event'}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {id ? 'Update your event details below.' : 'Fill in the details to submit your event for review.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Event Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Image
            </label>
            <ImageUpload
              bucket="events"
              currentImage={form.image_url}
              onUpload={(url) => setForm(prev => ({ ...prev, image_url: url }))}
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Title *
            </label>
            <input
              type="text"
              name="title"
              required
              value={form.title}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="e.g., Annual Youth Conference 2026"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description *
            </label>
            <textarea
              name="description"
              required
              rows={4}
              value={form.description}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="Describe your event..."
            />
          </div>

          {/* Event Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Event Type *
            </label>
            <select
              name="event_type"
              value={form.event_type}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="in_person">In-Person</option>
              <option value="virtual">Virtual/Online</option>
              <option value="hybrid">Hybrid (In-Person + Virtual)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {form.event_type === 'in_person' && 'Physical event at a venue'}
              {form.event_type === 'virtual' && 'Online event only'}
              {form.event_type === 'hybrid' && 'Both in-person and virtual attendance options'}
            </p>
          </div>

          {/* Virtual Event Details */}
          {(form.event_type === 'virtual' || form.event_type === 'hybrid') && (
            <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Virtual Event Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Virtual Platform</label>
                  <select
                    name="virtual_platform"
                    value={form.virtual_platform}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Platform</option>
                    <option value="Zoom">Zoom</option>
                    <option value="Microsoft Teams">Microsoft Teams</option>
                    <option value="Google Meet">Google Meet</option>
                    <option value="Webex">Webex</option>
                    <option value="YouTube Live">YouTube Live</option>
                    <option value="Facebook Live">Facebook Live</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Virtual Meeting URL</label>
                  <input
                    type="url"
                    name="virtual_url"
                    value={form.virtual_url}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="https://zoom.us/j/..."
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Meeting ID (Optional)</label>
                  <input
                    type="text"
                    name="virtual_meeting_id"
                    value={form.virtual_meeting_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="123 456 7890"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Passcode (Optional)</label>
                  <input
                    type="text"
                    name="virtual_passcode"
                    value={form.virtual_passcode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Enter passcode"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="start_date"
                required
                value={form.start_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={handleChange}
                min={form.start_date}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Organization
            </label>
            <select
              name="organization_id"
              value={form.organization_id}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="">Use Church Name ({church?.name || profile?.church_name || 'Your Church'})</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
              <option value="other">Other (specify below)</option>
            </select>
            {form.organization_id === 'other' && (
              <input
                type="text"
                name="custom_organization"
                value={form.custom_organization}
                onChange={handleChange}
                placeholder="Enter organization name"
                className="w-full mt-2 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            )}
          </div>

          {/* Audience & Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Audience</label>
              <select
                name="event_audience"
                value={form.event_audience}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="general">General/All Ages</option>
                <option value="singles">Singles</option>
                <option value="married">Married Couples</option>
                <option value="youth">Youth</option>
                <option value="children">Children</option>
                <option value="seniors">Seniors</option>
                <option value="ministers">Ministers/Pastors</option>
                <option value="women">Women</option>
                <option value="men">Men</option>
                <option value="families">Families</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Category</label>
              <select
                name="event_category"
                value={form.event_category}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="conference">Conference</option>
                <option value="retreat">Retreat</option>
                <option value="camp">Camp</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
                <option value="convention">Convention</option>
                <option value="rally">Rally</option>
                <option value="revival">Revival</option>
                <option value="training">Training</option>
                <option value="fellowship">Fellowship</option>
                <option value="outreach">Outreach</option>
                <option value="missions">Missions</option>
              </select>
            </div>
          </div>

          {/* Venue */}
          {form.event_type !== 'virtual' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Venue
              </label>
              <button
                type="button"
                onClick={prefillChurchAsVenue}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add New Venue
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <select
                  name="venue_id"
                  value={form.venue_id}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a venue or enter location</option>
                  {venues.map(venue => (
                    <option key={venue.id} value={venue.id}>{venue.name} - {venue.city}, {venue.state}</option>
                  ))}
                </select>
              </div>
              <div>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="Location (City, State)"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          </div>
          )}

          {/* URLs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Event Website
              </label>
              <input
                type="url"
                name="website_url"
                value={form.website_url}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Registration URL
              </label>
              <input
                type="url"
                name="registration_url"
                value={form.registration_url}
                onChange={handleChange}
                placeholder="https://..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Paid Event Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="is_paid_event"
                name="is_paid_event"
                checked={form.is_paid_event}
                onChange={handleChange}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label htmlFor="is_paid_event" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                This is a paid event
              </label>
              {!church?.stripe_onboarding_complete && form.is_paid_event && (
                <span className="ml-2 text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded">
                  Setup payments in Settings first
                </span>
              )}
            </div>

            {form.is_paid_event && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Registration Fee ($)
                  </label>
                  <input
                    type="number"
                    name="registration_fee"
                    value={form.registration_fee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Early Bird Fee ($)
                  </label>
                  <input
                    type="number"
                    name="early_bird_fee"
                    value={form.early_bird_fee}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Early Bird Deadline
                  </label>
                  <input
                    type="date"
                    name="early_bird_deadline"
                    value={form.early_bird_deadline}
                    onChange={handleChange}
                    max={form.start_date}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Max Attendees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Maximum Attendees (optional)
            </label>
            <input
              type="number"
              name="max_attendees"
              value={form.max_attendees}
              onChange={handleChange}
              min="1"
              placeholder="Leave blank for unlimited"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => navigate('/church/events')}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
          >
            {loading ? 'Saving...' : (id ? 'Update Event' : 'Submit for Review')}
          </button>
        </div>
      </form>

      {/* Info Box */}
      {!id && (
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-500 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">Review Process</h4>
              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                Your event will be reviewed by our team before being published. This usually takes 1-2 business days.
                You'll receive an email notification once your event is approved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add Venue Modal */}
      {showVenueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Venue</h2>
                <button
                  onClick={() => setShowVenueModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Add your church or another venue to use for events.
              </p>
            </div>

            <form onSubmit={handleAddVenue} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Venue Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={venueForm.name}
                  onChange={handleVenueFormChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., First Pentecostal Church"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  value={venueForm.address}
                  onChange={handleVenueFormChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="123 Main Street"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    required
                    value={venueForm.city}
                    onChange={handleVenueFormChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="St. Louis"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    name="state"
                    required
                    value={venueForm.state}
                    onChange={handleVenueFormChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="MO"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    name="zip_code"
                    value={venueForm.zip_code}
                    onChange={handleVenueFormChange}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="63101"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Capacity
                  </label>
                  <input
                    type="number"
                    name="capacity"
                    value={venueForm.capacity}
                    onChange={handleVenueFormChange}
                    min="1"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Website
                </label>
                <input
                  type="url"
                  name="website_url"
                  value={venueForm.website_url}
                  onChange={handleVenueFormChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  rows={2}
                  value={venueForm.description}
                  onChange={handleVenueFormChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Brief description of the venue..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowVenueModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingVenue}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                >
                  {savingVenue ? 'Adding...' : 'Add Venue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurchCreateEventPage;
